import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { vendorsApi } from '../api/vendorsApi'
import { useGetPurchaseHistories } from '@/hooks/usePurchaseHistory'
import type { Vendor } from '../types/vendor'
import type { CreateVendorProductPayload } from '../types/vendorProduct'

export const useGetVendors = (name?: string , enabled = true) => {
  return useQuery({
    queryKey: ['vendors', name],
    queryFn: () => vendorsApi.getVendors(name),
    enabled
  })
}

export const useGetVendorProducts = (vendorId?: string, enabled = true) => {
  return useQuery({
    queryKey: ['vendors', vendorId, 'products'],
    queryFn: () => vendorsApi.getVendorProducts(vendorId!),
    enabled: enabled && !!vendorId,
  })
}

export const useGetVendorItems = (vendorId?: string, enabled = true) => {
  return useQuery({
    queryKey: ['vendors', vendorId, 'items'],
    queryFn: () => vendorsApi.getVendorItems(vendorId!),
    enabled: enabled && !!vendorId,
  })
}

export const useGetVendorPurchaseHistories = (vendorId?: string, enabled = true) => {
  return useGetPurchaseHistories({ vendorId }, enabled && !!vendorId)
}

export const useCreateVendor = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (vendor: Pick<Vendor, 'name' | 'contactId' | 'phone' | 'email'>) => vendorsApi.createVendor(vendor),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
    },
  })
}

export const useCreateVendorProduct = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ vendorId, payload }: { vendorId: string; payload: CreateVendorProductPayload }) =>
      vendorsApi.createVendorProduct(vendorId, payload),
    onSuccess: (_data, { vendorId }) => {
      queryClient.invalidateQueries({ queryKey: ['vendors', vendorId, 'products'] })
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
    },
  })
}

export const useUpdateVendor = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ id, vendor }: { id: string; vendor: Partial<Vendor> }) => vendorsApi.updateVendor(id, vendor),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
    },
  })
}

export const useDeleteVendor = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: (id: string) => vendorsApi.deleteVendor(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['vendors'] })
    },
  })
}
